import { Briefcase } from 'lucide-react'
import type {
  InputDefinition,
  ParameterDefinition,
  UniversalNodeSchema,
} from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'
import { entryWindowInput, entryWindowParameters } from './entry-window-schema'
import { entryRulesSchema } from './strategy-schemas'
import { lowerTradeManagers } from '@/features/backtest/utils/lower-trade-managers'

/**
 * The Trade Manager: one node for the whole trade side, in labelled sections
 * (Trigger, Entry, Target, Stop, Risk, Advanced) instead of Simple Trade's
 * fixed percents or Advanced Trade's five satellite nodes.
 *
 * Each section's type select picks how it is set. Typed modes (PnL %, Ticks)
 * need nothing wired; the modes that read another node (a price level, a
 * condition, a Custom node) bring that section's socket out, through the
 * shared `activeWhen` rule. Position size and risk are fields here because the
 * Position Sizing node never fed anything but a trade.
 *
 * How the strategy is RUN (sizing mode, cash, costs) lives on
 * the Run Settings node it feeds through its Trades output (report-schema.ts).
 *
 * The engine never sees this node. `buildEngineStrategyGraph` lowers it into
 * the Advanced Trade cluster the engine already runs (utils/lower-trade-managers.ts).
 * See docs/superpowers/specs/2026-10-05-backtest-node-first-design.md.
 */

export const TRADE_MANAGER_TYPE = 'tradeManager'

const PRICE_FIELDS = [
  { value: 'close', label: 'Close' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
  { value: 'open', label: 'Open' },
]

const inputs: InputDefinition[] = [
  {
    id: 'trigger',
    hideLabel: true,
    emptyText: 'Connect an entry signal',
    build: 'Build entry',
    displayName: 'Trigger',
    acceptedTypes: ['boolean'],
    acceptedSources: ['*'],
  },
  {
    id: 'limitPrice',
    hideLabel: true,
    emptyText: 'Connect a limit price',
    displayName: 'Limit price',
    acceptedTypes: ['number', 'ohlcv'],
    acceptedSources: ['*'],
    activeWhen: { parameter: 'entryType', is: ['Limit'] },
    fieldSelection: { options: PRICE_FIELDS, defaultField: 'close' },
  },
  {
    id: 'targetLevel',
    hideLabel: true,
    emptyText: 'Connect a target price',
    displayName: 'Level',
    acceptedTypes: ['number', 'ohlcv'],
    acceptedSources: ['*'],
    activeWhen: { parameter: 'targetType', is: ['Static Value'] },
    fieldSelection: { dynamic: true, defaultField: 'close', fallbackOptions: PRICE_FIELDS },
  },
  {
    id: 'targetWhen',
    hideLabel: true,
    emptyText: 'Connect an exit condition',
    displayName: 'Exit when',
    acceptedTypes: ['boolean'],
    acceptedSources: ['*'],
    activeWhen: { parameter: 'targetType', is: ['Condition'] },
  },
  {
    // Multi Target: several targets, each closing part of the position. A
    // plain Target node would only repeat the row's own modes, so only a
    // Multi Target plugs in. The setting keeps its stored value, 'Custom'.
    id: 'targetCustom',
    hideLabel: true,
    emptyText: 'Connect a Multi Target node',
    displayName: 'Multi Target',
    acceptedTypes: ['number'],
    acceptedSources: ['entry-exit-logic'],
    nodeTypeFilter: ['multiTarget'],
    activeWhen: { parameter: 'targetType', is: ['Custom'] },
  },
  {
    id: 'stopLevel',
    hideLabel: true,
    emptyText: 'Connect a stop price',
    displayName: 'Level',
    acceptedTypes: ['number', 'ohlcv'],
    acceptedSources: ['*'],
    activeWhen: { parameter: 'stopType', is: ['Static Value'] },
    fieldSelection: { dynamic: true, defaultField: 'close', fallbackOptions: PRICE_FIELDS },
  },
  {
    // The Trailing Stop node's Condition mode (Chris, 2026-10-07): closes the
    // trade at market once true, after the activation allows it.
    id: 'trailWhen',
    hideLabel: true,
    emptyText: 'Connect an exit condition',
    displayName: 'Trail exit when',
    acceptedTypes: ['boolean'],
    acceptedSources: ['*'],
    activeWhen: { parameter: 'trailType', is: ['Condition'] },
    activeWhenAll: [{ parameter: 'enableTrailing', is: [true] }],
  },
  {
    // Chris, 2026-10-07: the From / Until times are the window, so a socket
    // for it as well was the same thing twice. A saved wire still works.
    ...entryWindowInput,
    retired: 'set the window with entryWindow, entryWindowStart and entryWindowEnd.',
  },
]

/** Entry Rules' Limit expiration fields, unchanged (they only show for Limit). */
const expirationParameters = entryRulesSchema.parameters.filter((p) =>
  p.name.startsWith('expiration'),
)

const trailingOn = { parameter: 'enableTrailing', values: [true] }

const parameters: ParameterDefinition[] = [
  // Entry
  {
    name: 'orderDirection',
    displayName: 'Direction',
    type: 'select',
    default: 'Long',
    options: [
      { value: 'Long', label: 'Long' },
      { value: 'Short', label: 'Short' },
    ],
  },
  {
    name: 'entryType',
    displayName: 'Order',
    type: 'select',
    default: 'Market',
    options: [
      { value: 'Market', label: 'Market' },
      { value: 'Limit', label: 'Limit' },
    ],
  },
  {
    name: 'offset',
    displayName: 'Price Offset',
    type: 'float',
    default: 0,
    dependsOn: { parameter: 'entryType', values: ['Limit'] },
  },
  // Target
  {
    name: 'targetType',
    displayName: 'Type',
    type: 'select',
    default: '% Trade',
    options: [
      { value: '% Trade', label: 'PnL %' },
      { value: 'Ticks', label: 'Ticks' },
      { value: 'Static Value', label: 'Price level' },
      { value: 'Condition', label: 'Condition' },
      // Named for the node it takes (Chris, 2026-10-06).
      { value: 'Custom', label: 'Multi Target' },
    ],
  },
  {
    name: 'targetValue',
    displayName: 'Value',
    type: 'float',
    default: 2.0,
    min: 0.1,
    max: 1000,
    step: 0.1,
    dependsOn: { parameter: 'targetType', values: ['% Trade', 'Ticks'] },
  },
  {
    name: 'targetOffset',
    displayName: 'Price Offset',
    description: 'Added to the wired price, in dollars. Negative moves it below.',
    type: 'float',
    default: 0,
    addLabel: 'Add offset',
    dependsOn: { parameter: 'targetType', values: ['Static Value'] },
  },
  // Stop
  {
    name: 'stopType',
    displayName: 'Type',
    type: 'select',
    default: '% Trade',
    options: [
      { value: '% Trade', label: 'PnL %' },
      { value: 'Ticks', label: 'Ticks' },
      { value: 'Static Value', label: 'Price level' },
    ],
  },
  {
    name: 'stopValue',
    displayName: 'Value',
    type: 'float',
    default: 1.0,
    min: 0.1,
    max: 1000,
    step: 0.1,
    dependsOn: { parameter: 'stopType', values: ['% Trade', 'Ticks'] },
  },
  {
    name: 'stopOffset',
    displayName: 'Price Offset',
    description: 'Added to the wired price, in dollars. Negative moves it below.',
    type: 'float',
    default: 0,
    addLabel: 'Add offset',
    dependsOn: { parameter: 'stopType', values: ['Static Value'] },
  },
  // Risk
  {
    name: 'sizingMethod',
    displayName: 'Sizing',
    type: 'select',
    default: 'Shares',
    options: [
      { value: 'Shares', label: 'Shares' },
      { value: 'Risk ($)', label: 'Risk ($)' },
      { value: 'Value ($)', label: 'Value ($)' },
      { value: '% Portfolio', label: '% Portfolio' },
    ],
  },
  {
    name: 'sizingValue',
    displayName: 'Value',
    type: 'float',
    default: 100,
  },
  // Advanced
  {
    // 0 = unlimited. When > 0 the strategy stops taking new entries after
    // this many; exits are unaffected.
    name: 'maxEntries',
    displayName: 'Max Entries',
    type: 'int',
    default: 0,
    min: 0,
  },
  ...entryWindowParameters,
  ...expirationParameters,
  {
    name: 'enableTrailing',
    displayName: 'Trailing Stop',
    type: 'boolean',
    default: false,
  },
  {
    name: 'trailType',
    displayName: 'Trail Type',
    type: 'select',
    default: '% Trade',
    options: [
      { value: '% Trade', label: '% Trade' },
      { value: 'Ticks', label: 'Ticks' },
      { value: 'Condition', label: 'Condition' },
    ],
    dependsOn: trailingOn,
  },
  {
    name: 'trailDistance',
    displayName: 'Trail Distance',
    type: 'float',
    default: 2.0,
    min: 0.1,
    max: 100,
    step: 0.1,
    dependsOn: trailingOn,
    dependsOnAll: [{ parameter: 'trailType', values: ['% Trade', 'Ticks'] }],
  },
  {
    name: 'activationTrigger',
    displayName: 'Activation',
    type: 'select',
    default: 'Immediate',
    options: [
      { value: 'Immediate', label: 'Immediate' },
      { value: 'Breakeven', label: 'Breakeven' },
      { value: 'Profit %', label: '% Profit' },
    ],
    dependsOn: trailingOn,
  },
  {
    name: 'activationValue',
    displayName: 'Profit % Value',
    type: 'float',
    default: 1.0,
    min: 0.1,
    max: 100,
    step: 0.1,
    dependsOn: trailingOn,
    dependsOnAll: [{ parameter: 'activationTrigger', values: ['Profit %'] }],
  },
]

export const tradeManagerSchema: UniversalNodeSchema = {
  type: TRADE_MANAGER_TYPE,
  // Lowered to the Advanced Trade cluster before any run. The AI builds with
  // it (the 2026-10-06 vocabulary swap); EdgeScript prints the cluster it
  // lowers to, so code never names it (schemas/compound.ts).
  compound: {
    engine: 'lowered',
    lower: lowerTradeManagers,
    placesTrades: true,
    singleton: { code: 'MULTIPLE_TRADE_MANAGERS', noun: 'Trade Manager', plural: 'Trade Managers' },
    catalog: { edgescript: false },
  },
  displayName: 'Trade Manager',
  category: 'entry-exit-logic',
  icon: Briefcase,
  description:
    'Places the trade: a trigger, the entry order, target, stop and position size, in one node.',
  aliases: ['trade', 'simple trade', 'advanced trade', 'position sizing'],
  // Every socket but the retired Window (last, optional) is required while its
  // section's settings use it (input-activity.ts gates the index rule).
  inputHandler: { type: 'multi', minInputs: inputs.length - 1, maxInputs: inputs.length },
  inputs,
  // Feeds the Report node: the chain's picture, never sent to the engine (the
  // Report node and its wire are editor-only).
  outputs: [{ id: 'trades', displayName: 'Trades', type: 'array' }],
  parameters,
  face: {
    // Halfway between a standard node and the old 330px (Chris, 2026-10-06).
    width: 'medium',
    inputControl: 'source',
    // Leaving a mode drops its wire: Limit back to Market removes the limit
    // price socket and what was wired into it.
    releaseInactive: true,
    shortName: 'Trade',
    // Trigger keeps its section (the pill alone: the heading names it), then
    // one titled section of paired rows, each with its sockets right under it
    // while a mode reads a wire (Chris, 2026-10-05). Rare settings sit under
    // a closed Advanced.
    sections: [
      { title: 'Trigger', inputs: ['trigger'] },
      {
        title: 'Trade settings',
        rows: [
          { label: 'Entry', params: ['orderDirection', 'entryType'], inputs: ['limitPrice'] },
          {
            label: 'Target',
            params: ['targetType', 'targetValue', 'targetOffset'],
            inputs: ['targetLevel', 'targetWhen', 'targetCustom'],
          },
          {
            label: 'Stop',
            params: ['stopType', 'stopValue', 'stopOffset'],
            inputs: ['stopLevel'],
          },
          { label: 'Risk', params: ['sizingMethod', 'sizingValue'] },
        ],
      },
      {
        title: 'Advanced',
        collapsible: true,
        // Drawn only while a saved wire holds it (retired), which also holds
        // Advanced open and hides the times it stands in for.
        inputs: [entryWindowInput.id],
        params: [
          'offset',
          'maxEntries',
          'entryWindow',
          ...expirationParameters.map((p) => p.name),
          'enableTrailing',
        ],
        // Each toggle with what it turns on, as one feature (Chris,
        // 2026-10-07): From to Until; the trail's type and distance, a
        // Condition's socket under them (its handle level with Trailing Stop,
        // as Target's with "Target"), then the activation.
        groups: [
          {
            toggle: 'entryWindow',
            rows: [{ label: '', params: ['entryWindowStart', 'entryWindowEnd'], joiner: 'to' }],
          },
          {
            toggle: 'enableTrailing',
            rows: [
              { label: '', params: ['trailType', 'trailDistance'], inputs: ['trailWhen'] },
              { label: 'Activation', params: ['activationTrigger', 'activationValue'] },
            ],
          },
        ],
      },
    ],
  },
}

export function registerTradeManagerSchema(): void {
  UniversalNodeRegistry.register(tradeManagerSchema)
}
